/**
 * DashboardView Component
 *
 * Main dashboard page with header, date range selector, and content sections.
 * Provides task overview and statistics for the selected workspace.
 *
 * @module pages/DashboardPage
 */

import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useDeviceLayout,
  useWorkspaceSwitcher,
  useDashboardSummary,
  useDashboardRecentTasks,
  useDashboardDelayedTasks,
} from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { selectCurrentWorkspace } from '@/store/selectors/authSelectors';
import { mapRoutes } from '@/routes/routes';
import { TaskStatus } from '@/types';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { PageHeader } from '@/components/common/PageHeader';
import { WorkspaceSelector } from '@/components/layout/AppShell/TopBar/WorkspaceSelector';
import { DateRangeSelector } from './components/DateRangeSelector';
import { TaskOverviewSection } from './components/TaskOverviewSection';
import { PrioritySection } from './components/PrioritySection';
import { TeamWorkloadSection } from './components/TeamWorkloadSection';
import { RecentTasksSection } from './components/RecentTasksSection';
import { DelayedTasksSection } from './components/DelayedTasksSection';
import styles from './DashboardView.module.css';

export const DashboardView = memo(function DashboardView() {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  const handleWorkspaceSwitch = useWorkspaceSwitcher('dashboard');

  const dateRange = useGeoMarkupStore((state) => state.ui.dashboard.dateRange);
  const setDashboardDateRange = useGeoMarkupStore(
    (state) => state.ui.setDashboardDateRange
  );

  const {
    taskStats,
    priorityDistribution,
    totalTasks,
    teamWorkload,
    isLoading: isSummaryLoading,
  } = useDashboardSummary();
  const { recentTasks, isLoading: isRecentLoading } = useDashboardRecentTasks();
  const {
    delayedTasks,
    totalCount: delayedTotalCount,
    isLoading: isDelayedLoading,
  } = useDashboardDelayedTasks();

  const navigate = useNavigate();

  const handleShowAllDelayed = useCallback(() => {
    const workspace = selectCurrentWorkspace(useGeoMarkupStore.getState());
    if (!workspace) return;
    const { taskFilters } = useGeoMarkupStore.getState();
    taskFilters.resetFilters();
    taskFilters.setStatus([TaskStatus.Delayed]);
    // URL is the source of truth for view mode — navigate with `?view=list`
    // instead of writing the mirror directly. `useMapViewMode` then fires
    // `syncViewMode` on the URL change, which updates the persistence
    // mirror and ultimately the backend user preferences.
    navigate(`${mapRoutes.background('tasks', workspace.shortId)}?view=list`);
  }, [navigate]);

  return (
    <AppScrollArea fill>
      <div className={styles.dashboard}>
        {isMobile && (
          <WorkspaceSelector
            className={styles.workspaceSelector}
            onWorkspaceSwitch={handleWorkspaceSwitch}
            fullWidth
          />
        )}
        <PageHeader
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitles.zadania')}
          size="lg"
          actions={
            <DateRangeSelector
              value={dateRange}
              onChange={setDashboardDateRange}
            />
          }
        />
        <div className={styles.content}>
          <div className={styles.topGrid}>
            <TaskOverviewSection
              dateRange={dateRange}
              taskStats={taskStats}
              isLoading={isSummaryLoading}
            />
            <PrioritySection
              priorities={priorityDistribution ?? []}
              totalTasks={totalTasks ?? 0}
              isLoading={isSummaryLoading}
            />
          </div>
          <div className={styles.bottomGrid}>
            <TeamWorkloadSection
              members={teamWorkload ?? []}
              isLoading={isSummaryLoading}
            />
            <RecentTasksSection
              tasks={recentTasks}
              isLoading={isRecentLoading}
            />
          </div>
          <DelayedTasksSection
            tasks={delayedTasks}
            totalCount={delayedTotalCount}
            onShowAll={handleShowAllDelayed}
            isLoading={isDelayedLoading}
          />
        </div>
      </div>
    </AppScrollArea>
  );
});

DashboardView.displayName = 'DashboardView';
